import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { AppLocale } from "../i18n/types";
import { useLocale } from "../context/LocaleContext";

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const removeMetaByProperty = (property: string) => {
  document.querySelectorAll(`meta[property="${property}"]`).forEach((el) => el.remove());
};

const upsertCanonical = (href: string) => {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }
  element.setAttribute("rel", "canonical");
  element.setAttribute("href", href);
};

/** Stessa URL, contenuto negoziato per lingua: hreflang aiuta Google a capire le varianti linguistiche. */
const syncHreflangAlternates = (canonical: string) => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  const pairs: [string, string][] = [
    ["it", canonical],
    ["en", canonical],
    ["x-default", canonical],
  ];
  for (const [hreflang, href] of pairs) {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflang);
    link.setAttribute("href", href);
    document.head.appendChild(link);
  }
};

/** Open Graph: locale principale + alternate per l’altra lingua (stesso URL). */
const syncOgLocaleAlternates = (locale: AppLocale) => {
  removeMetaByProperty("og:locale:alternate");
  const alternate = locale === "en" ? "it_IT" : "en_US";
  const m = document.createElement("meta");
  m.setAttribute("property", "og:locale:alternate");
  m.setAttribute("content", alternate);
  document.head.appendChild(m);
};

const upsertJsonLd = (id: string, payload: Record<string, unknown>) => {
  let script = document.head.querySelector(`#${id}`) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(payload);
};

const SeoManager = () => {
  const location = useLocation();
  const { locale, bundle, config } = useLocale();

  useEffect(() => {
    const seo = bundle.seoByPath[location.pathname] ?? bundle.seoByPath["/"];
    const homeSeo = bundle.seoByPath["/"];
    const siteName = `${config.name} ${config.surname} | ${config.metaSiteBrandSuffix}`;
    const siteUrl = config.siteUrl.replace(/\/$/, "");
    const origin = typeof window !== "undefined" ? window.location.origin : siteUrl;
    const canonical = `${origin}${location.pathname === "/" ? "" : location.pathname}`;
    const defaultOgImage = `${siteUrl}/logo512.png`;
    const personId = `${siteUrl}/#person`;
    const professionalServiceId = `${siteUrl}/#professional-service`;
    const htmlLang = locale === "en" ? "en" : "it";
    const ogLocale = locale === "en" ? "en_US" : "it_IT";
    const inLanguage = locale === "en" ? "en-US" : "it-IT";

    document.documentElement.lang = htmlLang;
    document.title = seo.title;

    upsertMeta('meta[name="description"]', { name: "description", content: seo.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" });
    upsertMeta('meta[name="author"]', { name: "author", content: `${config.name} ${config.surname}` });
    upsertMeta('meta[name="geo.region"]', { name: "geo.region", content: "IT-PD" });

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: seo.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: ogLocale });
    syncOgLocaleAlternates(locale);
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: defaultOgImage });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "512" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "512" });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: `${config.name} ${config.surname} — ${config.role}`,
    });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seo.title });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: seo.description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: defaultOgImage });

    upsertCanonical(canonical);
    syncHreflangAlternates(canonical);

    upsertJsonLd("ld-json-graph", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          name: siteName,
          url: siteUrl,
          inLanguage,
          description: homeSeo.description,
          publisher: { "@id": personId },
        },
        {
          "@type": "WebPage",
          "@id": `${canonical}#webpage`,
          url: canonical,
          name: seo.title,
          description: seo.description,
          inLanguage,
          isPartOf: { "@id": `${siteUrl}/#website` },
          about: { "@id": professionalServiceId },
        },
        {
          "@type": "Person",
          "@id": personId,
          name: `${config.name} ${config.surname}`,
          url: siteUrl,
          email: config.email,
          telephone: config.phone,
          jobTitle: config.role,
          knowsAbout: config.skills,
          sameAs: [config.social.linkedin, config.social.github],
          address: {
            "@type": "PostalAddress",
            addressLocality: config.locationDisplay,
            addressCountry: "IT",
          },
        },
        {
          "@type": "ProfessionalService",
          "@id": professionalServiceId,
          name: config.professionalServiceName,
          description: config.professionalServiceDescription,
          url: siteUrl,
          areaServed: { "@type": "Country", name: config.countryServed },
          provider: { "@id": personId },
        },
      ],
    });
  }, [location.pathname, locale, bundle, config]);

  return null;
};

export default SeoManager;
